// Pure view-model for the ROI summary (stories 64-69). Reads the engine's rollup through the same
// formatters as the deck, so the number on screen is the number on the slide. Realization and
// attribution are shown, never edited, here (story 69).

import { getSizedUseCase } from '../../data/use-case-catalog';
import { money, moneyBig, num, returnMultipleText } from '../../engine/format';
import { computeCalculator, type CalculatorInput } from '../../engine/use-case-engine';
import { TOTAL_ATTRIBUTED_LABEL, attributedLabelOf } from './attributed-label';
import { FORMULA_LABEL, ROI_ABSENT_COPY } from './deck/deck-view-model';

export { FORMULA_LABEL, ROI_ABSENT_COPY };

export interface RoiLineVm {
  id: string;
  name: string;
  tier: string;
  grossText: string;
  realizationText: string;
  programText: string;
  attributionText: string;
  /** Realization / attribution still on the use case's default (no stored answer): shown dashed, as Assumed. */
  realizationAssumed: boolean;
  attributionAssumed: boolean;
  attributedText: string;
  /** "Clay-attributed revenue" / "Clay-attributed cost savings", by the use case's outcome. */
  attributedLabel: string;
}

export type RoiTile =
  | { kind: 'roi'; text: string; basisText: string }
  /** Blank estimate: the tile says so and holds the estimate field right there. */
  | { kind: 'absent'; copy: string };

/** One bar of the Clay-attributed value chart (story 18). */
export interface RoiBarVm {
  id: string;
  name: string;
  tier: string;
  /** This use case's Clay-attributed figure (the bar's length). */
  valueText: string;
  /** "Clay-attributed revenue" / "Clay-attributed cost savings". */
  valueLabel: string;
  /** Program value beside it. */
  programText: string;
  /** Bar length as a share of the longest bar, 0-100 (a use case worth $0 draws no bar). */
  widthPct: number;
}

export interface RoiSummaryVm {
  /** Shown small above the equation so the realization step stays auditable. */
  grossText: string;
  programText: string;
  attributedText: string;
  /** Selected use cases (the hero slab's "across N use cases"). */
  useCaseCount: number;
  /** Attribution across the selected use cases: `25%`, or `25–60%` when it varies. */
  attributionText: string;
  /** The exact equation label (story 65). */
  formulaLabel: string;
  roi: RoiTile;
  /** Secondary line; null when no selected use case creates pipeline. */
  pipelineText: string | null;
  lines: RoiLineVm[];
  /** One bar per selected use case, in tier order; length is Clay-attributed value. */
  bars: RoiBarVm[];
  /** Deal-level inputs a selected use case still needs (its value counts as 0 until entered). */
  missingInputs: string[];
  empty: boolean;
}

export function buildRoiSummaryVm(sizing: CalculatorInput): RoiSummaryVm {
  const { rollup } = computeCalculator(sizing);
  const estimate = Number(sizing.deal.contractEstimate);
  const hasEstimate = Number.isFinite(estimate) && estimate > 0;
  const pcts = rollup.lines.map((l) => l.attributionPct);
  const lo = Math.min(...pcts);
  const hi = Math.max(...pcts);
  const attributionText = pcts.length === 0 ? '—' : lo === hi ? `${num(lo)}%` : `${num(lo)}–${num(hi)}%`;
  const maxAttributed = Math.max(0, ...rollup.lines.map((l) => l.attributedValue));
  const roiText = hasEstimate ? returnMultipleText(rollup.attributed, estimate) : null;
  return {
    grossText: moneyBig(rollup.gross),
    programText: moneyBig(rollup.program),
    attributedText: moneyBig(rollup.attributed),
    useCaseCount: rollup.lines.length,
    attributionText,
    formulaLabel: FORMULA_LABEL,
    roi:
      roiText === null
        ? { kind: 'absent', copy: ROI_ABSENT_COPY }
        : { kind: 'roi', text: roiText, basisText: `${TOTAL_ATTRIBUTED_LABEL} ÷ ${money(estimate)} contract estimate` },
    pipelineText: rollup.pipeline > 0 ? `${money(rollup.pipeline)} pipeline created` : null,
    lines: rollup.lines.map((l) => {
      const uc = getSizedUseCase(l.id);
      const sel = sizing.selected.find((x) => x.id === l.id);
      const onDefault = (v: number | '' | null | undefined) => v == null || v === '';
      return {
        id: l.id,
        name: uc?.name ?? l.id,
        tier: uc?.tier ?? '',
        grossText: money(l.value),
        realizationText: `${num(l.realizationPct)}%`,
        programText: money(l.programValue),
        attributionText: `${num(l.attributionPct)}%`,
        realizationAssumed: onDefault(sel?.realization),
        attributionAssumed: onDefault(sel?.attribution),
        attributedText: money(l.attributedValue),
        attributedLabel: attributedLabelOf(l.id),
      };
    }),
    bars: rollup.lines.map((l) => {
      const uc = getSizedUseCase(l.id);
      return {
        id: l.id,
        name: uc?.name ?? l.id,
        tier: uc?.tier ?? '',
        valueText: moneyBig(l.attributedValue),
        valueLabel: attributedLabelOf(l.id),
        programText: moneyBig(l.programValue),
        widthPct: maxAttributed > 0 ? Math.round((Math.max(0, l.attributedValue) / maxAttributed) * 1000) / 10 : 0,
      };
    }),
    missingInputs: rollup.missingInputs,
    empty: rollup.lines.length === 0,
  };
}
